<template>
  <div class="ragtag-container">
    <div class="content-section">
      <h1>Genome Assembly Improvement (RagTag)</h1>
      
      <div class="description">
        <h2>About RagTag</h2>
        <p>RagTag is a tool for improving genome assemblies, offering various functions to optimize and refine genomic sequences.</p>
        
        <div class="features">
          <h3>Main Functions:</h3>
          <ul>
            <li><strong>Correct:</strong> Homology-based sequence correction</li>
            <li><strong>Scaffold:</strong> Genome scaffolding</li>
            <li><strong>Patch:</strong> Genome patching</li>
            <li><strong>Merge:</strong> Scaffold merging</li>
          </ul>
        </div>

        <div class="processing-note">
          <el-icon><info-filled /></el-icon>
          Processing Time Note
          <p>Analysis may take several minutes to hours, depending on genome size and selected function.</p>
        </div>
      </div>

      <div class="key-features">
        <h2>Key Features:</h2>
        <ul>
          <li>Homology-based sequence correction for improved accuracy</li>
          <li>Multiple genome assembly optimization functions</li>
          <li>Suitable for genomes of various sizes</li>
          <li>Detailed analysis results and statistics</li>
          <li>Automatic parameter optimization</li>
          <li>Support for batch processing and parallel computation</li>
        </ul>
      </div>

      <!-- Upload Section -->
      <section>
        <h2>Upload Genome File</h2>
        <p class="upload-info">Please upload genome files in FASTA format (.fa, .fasta, .fna). Maximum file size: 500MB.</p>
        
        <!-- Function Selection -->
        <div class="function-select">
          <h3>Select Function</h3>
          <el-radio-group v-model="selectedFunction">
            <el-radio label="correct">Correct</el-radio>
            <el-radio label="scaffold">Scaffold</el-radio>
            <el-radio label="patch">Patch</el-radio>
            <el-radio label="merge">Merge</el-radio>
          </el-radio-group>
        </div>

        <!-- Upload Area -->
        <div class="upload-area">
          <!-- Reference Genome Upload -->
          <div class="upload-box">
            <h3>Reference Genome</h3>
            <el-upload
              class="upload-demo"
              drag
              :action="uploadUrl"
              :on-success="handleReferenceSuccess"
              :on-error="handleError"
              :before-upload="beforeUpload"
              :on-progress="handleProgress"
              :multiple="false"
              :limit="1"
              name="file"
              :data="{ type: 'reference' }"
            >
              <el-icon class="el-icon--upload"><upload-filled /></el-icon>
              <div class="el-upload__text">
                Drop file here or <em>click to upload</em>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  Supported formats: .fa, .fasta, .fna (max 500MB)
                </div>
              </template>
            </el-upload>
            <div v-if="uploadProgress.reference > 0 && uploadProgress.reference < 100" class="upload-progress">
              <el-progress :percentage="uploadProgress.reference" />
            </div>
          </div>

          <!-- AGP File Upload (for merge function) -->
          <div v-if="selectedFunction === 'merge'" class="upload-box">
            <h3>AGP Files</h3>
            <div class="upload-info">
              <el-alert
                title="Multiple AGP Files Required"
                type="info"
                description="The merge function requires at least 2 AGP files to combine multiple assemblies."
                show-icon
                :closable="false"
              />
            </div>
            <el-upload
              class="upload-demo"
              drag
              :action="uploadUrl"
              :on-success="handleAgpSuccess"
              :on-error="handleError"
              :before-upload="beforeAgpUpload"
              :on-progress="handleAgpProgress"
              :multiple="true"
              :limit="5"
              name="file"
              :data="{ type: 'agp' }"
            >
              <el-icon class="el-icon--upload"><upload-filled /></el-icon>
              <div class="el-upload__text">
                Drop AGP files here or <em>click to upload</em>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  Supported format: .agp (max 10MB per file)
                  <el-tooltip content="AGP (A Golden Path) file format is used to describe the assembly of a larger sequence from smaller fragments. At least 2 files are required for merge function.">
                    <el-icon><info-filled /></el-icon>
                  </el-tooltip>
                </div>
              </template>
            </el-upload>
            <div v-if="uploadProgress.agp > 0 && uploadProgress.agp < 100" class="upload-progress">
              <el-progress :percentage="uploadProgress.agp" />
            </div>
            <div v-if="getAgpFileCount < 2" class="file-count-warning">
              <el-alert
                title="More AGP Files Needed"
                type="warning"
                :description="'Currently have ' + getAgpFileCount + ' AGP file(s). Need at least 2 files for merge function.'"
                show-icon
                :closable="false"
              />
            </div>
          </div>

          <!-- Query Genome Upload (for non-merge functions) -->
          <div v-else class="upload-box">
            <h3>Query Genome</h3>
            <el-upload
              class="upload-demo"
              drag
              :action="uploadUrl"
              :on-success="handleQuerySuccess"
              :on-error="handleError"
              :before-upload="beforeUpload"
              :on-progress="handleProgress"
              :multiple="false"
              :limit="1"
              name="file"
              :data="{ type: 'query' }"
            >
              <el-icon class="el-icon--upload"><upload-filled /></el-icon>
              <div class="el-upload__text">
                Drop file here or <em>click to upload</em>
              </div>
              <template #tip>
                <div class="el-upload__tip">
                  Supported formats: .fa, .fasta, .fna (max 500MB)
                </div>
              </template>
            </el-upload>
            <div v-if="uploadProgress.query > 0 && uploadProgress.query < 100" class="upload-progress">
              <el-progress :percentage="uploadProgress.query" />
            </div>
          </div>
        </div>

        <!-- File List -->
        <div v-if="uploadedFiles.length > 0" class="file-list">
          <h3>Uploaded Files</h3>
          <el-table :data="uploadedFiles" style="width: 100%" border>
            <el-table-column prop="name" label="File Name" sortable />
            <el-table-column prop="type" label="Type" width="120" />
            <el-table-column prop="size" label="Size" width="120" sortable />
            <el-table-column prop="status" label="Status" width="120">
              <template #default="scope">
                <el-tag :type="scope.row.status === 'valid' ? 'success' : 'danger'">
                  {{ scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Actions" width="120">
              <template #default="scope">
                <el-button 
                  type="danger" 
                  size="small" 
                  @click="removeFile(scope.row)"
                  :icon="Delete"
                >
                  Remove
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- Parameters -->
        <div class="parameters-section">
          <h3>Parameters</h3>
          <el-form :model="parameters" label-width="180px">
            <!-- Correct Parameters -->
            <template v-if="selectedFunction === 'correct'">
              <el-form-item label="Minimum Match Length">
                <el-input-number v-model="parameters.minLen" :min="0" />
              </el-form-item>
              <el-form-item label="Minimum Identity">
                <el-input-number v-model="parameters.minIdentity" :min="0" :max="100" />
              </el-form-item>
            </template>
            
            <!-- Scaffold Parameters -->
            <template v-if="selectedFunction === 'scaffold'">
              <el-form-item label="Minimum Scaffold Length">
                <el-input-number v-model="parameters.minScaffoldLen" :min="0" />
              </el-form-item>
              <el-form-item label="Gap Length">
                <el-input-number v-model="parameters.gapLen" :min="0" />
              </el-form-item>
            </template>
          </el-form>
        </div>

        <!-- Submit Button -->
        <div class="submit-section">
          <el-button 
            type="primary" 
            @click="submitAnalysis"
            :loading="analyzing"
            :disabled="!canStartAnalysis"
            size="large"
          >
            Start Analysis
          </el-button>
        </div>

        <!-- Progress Display -->
        <div v-if="analyzing || error" class="status-section">
          <el-alert
            v-if="error"
            :title="error"
            type="error"
            :description="errorDetails"
            show-icon
            :closable="true"
            @close="clearError"
          />
          <div v-if="analyzing" class="progress-section">
            <el-progress 
              :percentage="analysisProgress" 
              :status="analysisProgress === 100 ? 'success' : ''"
            />
            <p class="progress-text">{{ progressText }}</p>
          </div>
        </div>
      </section>

      <!-- Results Display -->
      <div v-if="results" class="results-section">
        <h2>Analysis Results</h2>
        <div class="results-actions">
          <el-button 
            type="primary" 
            @click="downloadResults" 
            :loading="downloading"
            size="large"
          >
            Download Results
          </el-button>
        </div>

        <el-table
          :data="results"
          style="width: 100%"
          border
          stripe
        >
          <el-table-column prop="fileName" label="File Name" />
          <el-table-column prop="fileSize" label="File Size" />
          <el-table-column prop="description" label="Description" />
          <el-table-column label="Action" width="120">
            <template #default="scope">
              <el-button 
                type="primary" 
                size="small" 
                @click="downloadFile(scope.row)"
              >
                Download
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Citation and License -->
      <div class="citation-section">
        <h3>Citation and License</h3>
        <div class="citation-content">
          <p class="citation-text">
            This tool integrates RagTag, developed by Alonge et al. If you use this tool in your research, please cite:
          </p>
          <div class="citation-box">
            <p class="paper-citation">
              <strong>Primary Citation:</strong><br>
              Alonge M, Lebeigle L, Kirsche M, Jenike K, Ou S, Aganezov S, Wang X, Lippman ZB, Schatz MC, Soyk S. Automated assembly scaffolding using RagTag elevates a new tomato system for high-throughput genome editing. 
              <em>Genome Biology</em> 2022 Dec 15;23(1):258.
              <a href="https://doi.org/10.1186/s13059-022-02823-7" target="_blank">https://doi.org/10.1186/s13059-022-02823-7</a>
            </p>
            <p class="repo-citation">
              <strong>Software Repository:</strong><br>
              RagTag GitHub Repository: <a href="https://github.com/malonge/RagTag" target="_blank">https://github.com/malonge/RagTag</a>
            </p>
          </div>
          <div class="license-info">
            <p class="license-text">
              RagTag is licensed under the MIT License.
            </p>
            <p class="usage-note">
              This is an integration of RagTag and is not officially affiliated with the RagTag project. For the official implementation and latest updates, please visit the RagTag GitHub repository.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { UploadFilled, InfoFilled, Delete } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

export default {
  name: 'RagTag',
  components: {
    UploadFilled,
    InfoFilled
  },
  setup() {
    const selectedFunction = ref('correct')
    const analyzing = ref(false)
    const analysisProgress = ref(0)
    const progressText = ref('')
    const results = ref(null)
    const resultFileUrl = ref(null)
    const downloading = ref(false)
    const jobId = ref('')
    const error = ref('')
    const errorDetails = ref('')
    
    const parameters = ref({
      minLen: 1000,
      minIdentity: 95,
      minScaffoldLen: 1000,
      gapLen: 100
    })

    const uploadUrl = '/api/tools/ragtag/upload/'

    const uploadedFiles = ref([])
    const uploadProgress = ref({
      reference: 0,
      query: 0,
      agp: 0
    })

    // 计算 AGP 文件数量
    const getAgpFileCount = computed(() => {
      return uploadedFiles.value.filter(f => f.type === 'AGP').length
    })

    // 修改文件验证函数
    const validateFiles = () => {
      console.log('Validating files...')
      console.log('Selected function:', selectedFunction.value)
      console.log('Uploaded files:', uploadedFiles.value)

      const referenceFile = uploadedFiles.value.find(f => f.type === 'Reference')
      console.log('Reference file:', referenceFile)

      if (!referenceFile) {
        console.log('Reference file missing')
        return false
      }

      if (selectedFunction.value === 'merge') {
        const agpFiles = uploadedFiles.value.filter(f => f.type === 'AGP')
        console.log('AGP files:', agpFiles)
        
        if (agpFiles.length < 2) {
          console.log('Not enough AGP files')
          return false
        }

        // 验证所有文件的状态
        return referenceFile.status === 'valid' && 
               agpFiles.every(f => f.status === 'valid')
      } else {
        const queryFile = uploadedFiles.value.find(f => f.type === 'Query')
        console.log('Query file:', queryFile)
        
        if (!queryFile) {
          console.log('Query file missing')
          return false
        }

        return referenceFile.status === 'valid' && queryFile.status === 'valid'
      }
    }

    // 计算属性：是否可以开始分析
    const canStartAnalysis = computed(() => {
      if (analyzing.value) {
        return false
      }
      return validateFiles()
    })

    const clearError = () => {
      error.value = ''
      errorDetails.value = ''
    }

    const submitAnalysis = async () => {
      clearError()
      
      if (!validateFiles()) {
        if (selectedFunction.value === 'merge' && getAgpFileCount.value < 2) {
          ElMessage.error('Please upload at least 2 AGP files for merge function')
        } else {
          ElMessage.error('Please ensure all required files are uploaded correctly')
        }
        return
      }

      try {
        analyzing.value = true
        analysisProgress.value = 0
        
        if (selectedFunction.value === 'merge') {
          progressText.value = 'Running RagTag merge...'
        } else {
          progressText.value = `Running RagTag ${selectedFunction.value}...`
        }
        
        const referenceFile = uploadedFiles.value.find(f => f.type === 'Reference')
        const requestData = {
          function: selectedFunction.value,
          referenceFile: {
            path: referenceFile.path
          },
          parameters: parameters.value
        }

        if (selectedFunction.value === 'merge') {
          const agpFiles = uploadedFiles.value.filter(f => f.type === 'AGP')
          requestData.agpFiles = agpFiles.map(f => ({ path: f.path }))
        } else {
          const queryFile = uploadedFiles.value.find(f => f.type === 'Query')
          requestData.queryFile = {
            path: queryFile.path
          }
        }

        const response = await fetch('/api/tools/ragtag/analyze/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        })

        const data = await response.json()
        console.log('Analysis response data:', data)
        
        if (response.ok) {
          jobId.value = data.jobId
          results.value = data.results.map(result => ({
            ...result,
            jobId: data.jobId,
            fileName: result.fileName || result.name,
            downloadUrl: `/api/tools/ragtag/download/${data.jobId}/ragtag_output/${result.fileName || result.name}`
          }))
          
          ElMessage.success('Analysis completed successfully')
          progressText.value = 'Analysis completed'
          analysisProgress.value = 100
        } else {
          throw new Error(data.message || 'Analysis failed')
        }
      } catch (error) {
        console.error('Analysis failed:', error)
        error.value = 'Analysis Failed'
        errorDetails.value = error.message || 'An unexpected error occurred'
        ElMessage.error(error.message || 'Analysis failed')
      } finally {
        analyzing.value = false
      }
    }

    const handleReferenceSuccess = (response) => {
      const file = response.data
      // 移除之前的参考基因组文件（如果存在）
      const index = uploadedFiles.value.findIndex(f => f.type === 'Reference')
      if (index !== -1) {
        uploadedFiles.value.splice(index, 1)
      }
      uploadedFiles.value.push({
        name: file.name,
        type: 'Reference',
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        status: 'valid',
        path: file.path
      })
      uploadProgress.value.reference = 100
      ElMessage.success('Reference genome uploaded successfully')
      console.log('Updated uploaded files:', uploadedFiles.value)
    }

    const handleQuerySuccess = (response) => {
      const file = response.data
      // 移除之前的查询基因组文件（如果存在）
      const index = uploadedFiles.value.findIndex(f => f.type === 'Query')
      if (index !== -1) {
        uploadedFiles.value.splice(index, 1)
      }
      uploadedFiles.value.push({
        name: file.name,
        type: 'Query',
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        status: 'valid',
        path: file.path
      })
      uploadProgress.value.query = 100
      ElMessage.success('Query genome uploaded successfully')
      console.log('Updated uploaded files:', uploadedFiles.value)
    }

    const handleError = (error) => {
      let errorMessage = 'Upload failed'
      if (error.response) {
        const response = error.response
        if (response.data && response.data.message) {
          errorMessage = response.data.message
        } else {
          errorMessage = `Upload failed: HTTP ${response.status}`
        }
      }
      // 重置进度
      Object.keys(uploadProgress.value).forEach(key => {
        uploadProgress.value[key] = 0
      })
      ElMessage.error(errorMessage)
    }

    const downloadResults = async () => {
      if (!results.value || results.value.length === 0) {
        ElMessage.warning('No results available to download');
        return;
      }
      
      downloading.value = true;
      try {
        // 下载所有文件
        for (const file of results.value) {
          await downloadFile(file);
          // 添加小延迟以避免浏览器限制
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        ElMessage.success('All files downloaded successfully');
      } catch (error) {
        console.error('Download error:', error);
        ElMessage.error('Failed to download some files');
      } finally {
        downloading.value = false;
      }
    }

    const downloadFile = async (file) => {
      try {
        const fileJobId = file.jobId || jobId.value;
        if (!fileJobId) {
          throw new Error('No job ID available');
        }

        const response = await axios.get(
          `/api/tools/ragtag/download/${fileJobId}/ragtag_output/${file.fileName}`,
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Error downloading ${file.fileName}:`, error);
        throw error;
      }
    }

    const beforeAgpUpload = (file) => {
      const isAGP = file.name.toLowerCase().endsWith('.agp')
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isAGP) {
        ElMessage.error('Please upload an AGP format file!')
        return false
      }
      if (!isLt10M) {
        ElMessage.error('AGP file size cannot exceed 10MB!')
        return false
      }
      return true
    }

    const handleAgpSuccess = (response) => {
      const file = response.data
      uploadedFiles.value.push({
        name: file.name,
        type: 'AGP',
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        status: 'valid',
        path: file.path
      })
      uploadProgress.value.agp = 100
      ElMessage.success('AGP file uploaded successfully')
      console.log('Updated uploaded files:', uploadedFiles.value)
    }

    const handleAgpProgress = (event) => {
      uploadProgress.value.agp = Math.round(event.percent)
    }

    const handleProgress = (event, type) => {
      uploadProgress.value[type] = Math.round(event.percent)
    }

    const removeFile = (file) => {
      const index = uploadedFiles.value.findIndex(f => f.name === file.name)
      if (index !== -1) {
        uploadedFiles.value.splice(index, 1)
        // 重置相应的进度
        if (file.type === 'AGP') {
          uploadProgress.value.agp = 0
        } else if (file.type === 'Reference') {
          uploadProgress.value.reference = 0
        } else {
          uploadProgress.value.query = 0
        }
        console.log('File removed, updated uploaded files:', uploadedFiles.value)
      }
    }

    const beforeUpload = (file) => {
      const isValidType = ['fa', 'fasta', 'fna'].includes(
        file.name.split('.').pop().toLowerCase()
      )
      const isLt500M = file.size / 1024 / 1024 < 500

      if (!isValidType) {
        ElMessage.error('Please upload a FASTA format file!')
        return false
      }
      if (!isLt500M) {
        ElMessage.error('File size cannot exceed 500MB!')
        return false
      }
      return true
    }

    return {
      selectedFunction,
      parameters,
      analyzing,
      analysisProgress,
      progressText,
      results,
      resultFileUrl,
      downloading,
      uploadUrl,
      canStartAnalysis,
      submitAnalysis,
      downloadResults,
      downloadFile,
      jobId,
      uploadedFiles,
      uploadProgress,
      beforeAgpUpload,
      handleAgpSuccess,
      handleAgpProgress,
      handleProgress,
      removeFile,
      Delete,
      handleReferenceSuccess,
      handleQuerySuccess,
      handleError,
      beforeUpload,
      error,
      errorDetails,
      clearError,
      getAgpFileCount
    }
  }
}
</script>

<style scoped>
.ragtag-container {
  padding: 20px;
}

.content-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
}

h1 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.description {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.description h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.description p {
  color: #666;
  line-height: 1.6;
}

.features {
  margin-top: 20px;
}

.features h3 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.features ul {
  list-style-type: none;
  padding-left: 0;
}

.features li {
  color: #666;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.features li::before {
  content: "•";
  color: #4169E1;
  position: absolute;
  left: 0;
}

.upload-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

.upload-info {
  color: #666;
  margin-bottom: 20px;
}

.progress-section {
  margin-top: 20px;
}

.progress-text {
  text-align: center;
  color: #666;
  margin-top: 10px;
}

.results-section {
  margin-top: 30px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.results-header h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 200px;
}

:deep(.el-upload__tip) {
  color: #666;
}

:deep(.el-progress-bar__inner) {
  transition: width 0.3s ease;
}

.citation-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.citation-section h3 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.citation-content {
  color: #666;
  line-height: 1.6;
}

.citation-box {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
  margin: 10px 0;
}

.citation-box a {
  color: #409EFF;
  text-decoration: none;
  word-break: break-all;
}

.citation-box a:hover {
  text-decoration: underline;
}

.paper-citation {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #e4e7ed;
  line-height: 1.6;
}

.paper-citation em {
  font-style: italic;
}

.repo-citation {
  margin-bottom: 0;
  line-height: 1.6;
}

.citation-box strong {
  color: #2c3e50;
}

.license-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.license-text {
  margin-top: 10px;
  font-style: italic;
}

.usage-note {
  margin-top: 10px;
  font-size: 0.9em;
  color: #909399;
}

.upload-area {
  display: flex;
  gap: 20px;
  margin: 20px 0;
}

.upload-box {
  flex: 1;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.upload-box h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #2c3e50;
}

.parameters-section {
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.submit-section {
  margin: 20px 0;
  text-align: center;
}

.status-section {
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.file-list {
  margin: 20px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.file-count-warning {
  margin-top: 15px;
}

:deep(.el-alert--info) {
  margin-bottom: 10px;
  background-color: #f0f9ff;
  color: #1d4ed8;
}

:deep(.el-alert--warning) {
  background-color: #fff7ed;
  color: #c2410c;
}

:deep(.el-upload) {
  width: 100%;
}

:deep(.el-upload-dragger) {
  width: 100%;
  height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

:deep(.el-upload-dragger .el-icon--upload) {
  margin: 0 0 10px;
}

:deep(.el-upload__text) {
  margin: 10px 0;
}

:deep(.el-upload__tip) {
  padding: 10px 0;
  text-align: center;
  font-size: 12px;
}

:deep(.el-icon--upload) {
  font-size: 48px;
}

.upload-progress {
  margin-top: 10px;
}
</style> 